/**
 * Gate queue ordering, shared by the crew Gate Queue page (which lists arrived
 * truck shipments) and the manager Gate Queue page (which lists gate check-in
 * rows). Both pages show the same trucks from different tables, so the order
 * has to come from one place or the yard office and the yard floor will call
 * different drivers next.
 *
 * A truck's place in the queue is the LATER of its appointment and its
 * check-in: it cannot be served before the slot it booked, and it cannot be
 * served before it physically arrived. So a truck that checks in early waits
 * for its slot rather than jumping ahead of an earlier appointment, and a truck
 * that shows up after its slot has passed loses that slot and takes its place
 * from when it actually arrived.
 */

import {
  GateCheckInStatus,
  InboundState,
  OutboundState,
  UserType,
} from "./constants";

// The gate statuses the order rule below turns on. Taken from constants rather
// than from gateCheckInUtils: that module talks to AppSync, and this one is the
// pure ordering both queue pages (and their tests) stand on.
const { DOCKED, CALLED, ARRIVED, DEPARTED } = GateCheckInStatus;

/** Epoch ms, or null for a missing/unparseable stamp. Stamps reach these pages
 * from several writers (the driver API, the manager forms, older rows), so they
 * are parsed rather than compared as strings — one stamp written with a numeric
 * offset instead of Z would otherwise sort nowhere near its real time. */
const toMs = (value) => {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
};

/**
 * The time the truck becomes servable: the later of its appointment and its
 * check-in. Falls back to whichever one exists — a walk-in has no appointment,
 * and a check-in row written before this field existed has no appointment
 * copied onto it.
 *
 * @returns {number|null} epoch ms, or null when neither stamp is usable
 */
export const gateServiceTime = (scheduledDateTime, checkInDateTime) => {
  const appointment = toMs(scheduledDateTime);
  const checkedIn = toMs(checkInDateTime);
  if (appointment === null) return checkedIn;
  if (checkedIn === null) return appointment;
  return Math.max(appointment, checkedIn);
};

/**
 * Every shipment one check-in row controls. An inbound row names one load; an
 * outbound row names the load the driver searched (`outboundId`) and, for an
 * LTL "ship with" truck, every linked load in `outboundIds`. Same rule as
 * groupCheckInShipmentIds in utils/shipWith — written out here rather than
 * imported because that module talks to AppSync and this one must stay pure
 * (see the header). Rows naming no shipment come back empty.
 */
const checkInShipmentIds = (checkIn) => {
  if (checkIn?.inboundId) return [checkIn.inboundId];
  if (checkIn?.outboundIds?.length)
    return [...new Set(checkIn.outboundIds.filter(Boolean))];
  return checkIn?.outboundId ? [checkIn.outboundId] : [];
};

/**
 * One check-in per shipment: the newest of any duplicates.
 *
 * A shipment holds a `gateCheckInId` lock that is meant to keep this to one
 * row, but the lock is released on every terminal status and reclaimed on the
 * next check-in, so a row left active by a failed write - or by a driver who
 * checked in while the office was writing a manual row - shows up alongside
 * the real one. Undeduped, the same truck appears twice in the queue and the
 * two entries disagree about who is next.
 *
 * The newest wins, the same rule fetchActiveGateCheckIn applies when a
 * dispatch tab picks the row it will edit, so every surface manages the same
 * one. Rows naming no shipment are kept as they are: there is nothing to
 * collide them with, and dropping one would hide a truck that is at the gate.
 *
 * A row for a linked truck is keyed under every load it controls, so a stale
 * single-load row left on one member collapses against the group row that
 * covers it. A row that wins under several keys is listed once.
 */
export const dedupeActiveCheckIns = (checkIns) => {
  const newestByShipment = new Map();
  const unkeyed = [];
  (checkIns || []).forEach((checkIn) => {
    if (!checkIn) return;
    const shipmentIds = checkInShipmentIds(checkIn);
    if (!shipmentIds.length) {
      unkeyed.push(checkIn);
      return;
    }
    shipmentIds.forEach((shipmentId) => {
      const held = newestByShipment.get(shipmentId);
      // Parsed rather than string-compared: these stamps come from the driver
      // API, the manager forms and older rows, and one written with a numeric
      // offset would lose to any Z-suffixed stamp on text order alone.
      if (
        !held ||
        (toMs(held.arrivedAt) ?? 0) < (toMs(checkIn.arrivedAt) ?? 0)
      )
        newestByShipment.set(shipmentId, checkIn);
    });
  });
  const kept = [];
  const seen = new Set();
  newestByShipment.forEach((checkIn) => {
    if (seen.has(checkIn)) return;
    seen.add(checkIn);
    kept.push(checkIn);
  });
  return [...kept, ...unkeyed];
};

/**
 * Sorts a copy of `items` into gate queue order.
 *
 * @param {Array} items
 * @param {(item: any) => [any, any]} stampsOf returns
 *   [scheduledDateTime, checkInDateTime] for one item
 */
export const sortByGateServiceTime = (items, stampsOf) =>
  [...items]
    .map((item) => {
      const [scheduled, checkedIn] = stampsOf(item);
      return {
        item,
        serviceTime: gateServiceTime(scheduled, checkedIn),
        checkedIn: toMs(checkedIn),
      };
    })
    .sort((a, b) => {
      // A row with no usable stamp sorts last: it carries no claim to a place
      // in line, and sorting it first would park it at the front of the queue.
      if (a.serviceTime === null || b.serviceTime === null)
        return (
          (a.serviceTime === null ? 1 : 0) - (b.serviceTime === null ? 1 : 0)
        );
      if (a.serviceTime !== b.serviceTime) return a.serviceTime - b.serviceTime;
      // Same slot: whoever arrived first goes first.
      if (a.checkedIn !== null && b.checkedIn !== null)
        return a.checkedIn - b.checkedIn;
      return 0;
    })
    .map((entry) => entry.item);

/**
 * Whether the queue order binds this user's advances.
 *
 * A SUPERVISOR works the gate on the order the office published: they call the
 * next truck in line, they do not choose which truck that is. A MANAGER (and an
 * ADMIN) owns the schedule the order comes from, so calling a truck out of turn
 * is theirs to do — the order is shown to them, not enforced on them. CREW never
 * advance a check-in at all: they place the driver a supervisor already called,
 * so CALLED, not the order, is what binds them.
 */
export const isBoundByQueueOrder = (userType) =>
  userType === UserType.SUPERVISOR;

/**
 * The check-in whose turn it is to be called: the first row in queue order still
 * waiting at the gate. Rows already CALLED or DOCKED are through the gate and
 * no longer hold the front, so a queue whose first two trucks are at the docks
 * still lets the third be called.
 *
 * @param rows items already in queue order (sortByGateServiceTime)
 * @param statusOf reads the gate status off one item
 * @returns the row, or null when nobody is waiting
 */
export const nextInGateQueue = (rows, statusOf) =>
  (rows || []).find((row) => statusOf(row) === ARRIVED) || null;

/**
 * Whether this user may move this check-in to `status` right now.
 *
 * Only advancing a waiting driver is ordered — CALLED (or straight to DOCKED,
 * which calls and docks in one move) has to go to the truck at the front. Every
 * other move is free:
 *   - a row already CALLED or DOCKED was let in in turn, so finishing its visit
 *     is not a queue decision;
 *   - DEPARTED and CANCELLED end a visit rather than granting one — a no-show
 *     at the front must be cancellable, or the whole queue behind it stalls;
 *   - re-saving the status the row already carries is a dock edit, not a move.
 */
export const canSetGateStatus = ({
  status,
  currentStatus,
  orderBound,
  isNextInQueue,
}) => {
  if (!orderBound) return true;
  if (status === currentStatus) return true;
  if (status !== CALLED && status !== DOCKED) return true;
  if (currentStatus !== ARRIVED) return true;
  return Boolean(isNextInQueue);
};

/** Why a row cannot be advanced yet, naming the truck that goes first. Shared by
 * both queue pages so the yard office and the yard floor give the same reason. */
export const outOfTurnTooltip = (frontLabel) =>
  `Waiting on ${frontLabel || "the truck"} ahead in the queue`;

/**
 * Whether this row may be moved to DEPARTED right now.
 *
 * Not an order rule, and not about who is asking: DEPARTED is a claim about
 * what happened at the gate, so it binds a manager exactly as it binds a
 * supervisor. A truck departs after it reached a dock and was worked, which
 * makes DOCKED the only status DEPARTED can be reached from. A truck that never
 * got a dock — a no-show at the front, a driver who gave up waiting, one sent
 * away — left without a visit. CANCELLED is what ends that row, and it stays
 * open from every active status precisely so the queue never stalls behind
 * such a truck.
 *
 * Left open, this reads in the gate history as a dock visit that never
 * happened: saveGateCheckIn stamps completedAt, the shipment lock is released,
 * and the row carries no placement and no dock assignment to contradict it —
 * so the only record of the truck's actual visit is a departure. CALLED is
 * refused for the same reason as ARRIVED: being called forward is not being
 * worked, and allowing it would leave the identical row two clicks away.
 *
 * Re-saving DEPARTED on a row that already carries it is a records edit — the
 * dock or the plate the office is correcting after the fact — not a new claim.
 */
export const canSetGateDeparture = ({ status, currentStatus }) => {
  if (status !== DEPARTED) return true;
  if (currentStatus === DEPARTED) return true;
  return currentStatus === DOCKED;
};

/**
 * Whether the load a docked truck came for has been worked: the crew stamped
 * an end time, or the shipment reached its COMPLETED state (the two are set
 * by different paths — the process page stamps the time, the review and the
 * SIT save set the state — so either one is the answer). Both directions use
 * the same field and the same state value.
 *
 * A truck departs after its load is worked, so DEPARTED asks this of the
 * shipment as well as DOCKED of the row. A departure written over an
 * unworked load is the same false history as one written over an undocked
 * truck: the gate says the visit is over while the yard still holds the load.
 */
export const isShipmentWorkDone = (shipment) =>
  Boolean(shipment?.endDateTime) ||
  shipment?.state === InboundState.COMPLETED ||
  shipment?.state === OutboundState.COMPLETED;

/**
 * Whether every load a truck came for has been worked. One check-in row can
 * control several linked outbound loads (LTL "ship with"), and the truck
 * departs once, so DEPARTED asks this of all of them: a departure written
 * while one load is still on the dock is the same false history as one over
 * a single unworked load. An empty list has nothing to refuse on — the caller
 * decides whether "no loads read" means "ask the write guard".
 */
export const isGroupWorkDone = (shipments) =>
  (shipments || []).every(isShipmentWorkDone);

/**
 * Why this row may not be moved to DEPARTED, or null when it may. The row's
 * own status first (the truck has to be at a dock), then the load
 * (`shipment`, when the caller has it — a check-in naming no shipment, or a
 * surface that has not read it, passes null and the write guard settles it).
 * A caller holding every load of a linked truck passes `shipments` instead,
 * and all of them have to be worked. A row already DEPARTED is a records edit
 * and passes both.
 */
export const gateDepartureRefusal = ({
  status,
  currentStatus,
  shipment,
  shipments,
}) => {
  if (status !== DEPARTED || currentStatus === DEPARTED) return null;
  if (!canSetGateDeparture({ status, currentStatus }))
    return departureNeedsDockReason();
  const loads = shipments ?? (shipment ? [shipment] : []);
  if (loads.length && !isGroupWorkDone(loads))
    return departureNeedsWorkDoneReason();
  return null;
};

/** Why a move to DEPARTED was refused, and what to do instead. Not shown as
 * a tooltip: the select greys DEPARTED out without comment. It reaches the
 * user as a notification in two cases — a write that fails because the row
 * moved since it was drawn (GateDepartureNotDockedError), and a dispatch tab
 * whose Time Released stamp staged DEPARTED for a driver still on the way
 * (stagedGateEditRefusal). Plain words, since the reader is a manager mid-save,
 * and it is followed by a suffix in some surfaces, so it ends without a stop. */
export const departureNeedsDockReason = () =>
  "The driver has not been docked yet. Set Docked first, or cancel the check-in if the truck never came in";

/** The same, for a docked truck whose load is still being worked. */
export const departureNeedsWorkDoneReason = () =>
  "The load is still being worked. Set the end time or complete the shipment before marking the driver departed";

/**
 * The single door a status select goes through: why this row may not move to
 * `status`, or null when it may.
 *
 * Order and departure are separate rules with separate reasons, and a surface
 * that asked only one of them offered a move the write behind it refuses.
 * Departure is asked first — it is a fact about the row (and, with `shipment`
 * or the linked truck's `shipments` given, about the load) rather than about
 * the viewer, so it is the answer that does not change when a manager looks.
 */
export const gateStatusRefusal = ({
  status,
  currentStatus,
  orderBound,
  isNextInQueue,
  nextLabel,
  shipment = null,
  shipments,
}) => {
  const departure = gateDepartureRefusal({
    status,
    currentStatus,
    shipment,
    shipments,
  });
  if (departure) return departure;
  if (!canSetGateStatus({ status, currentStatus, orderBound, isNextInQueue }))
    return outOfTurnTooltip(nextLabel);
  return null;
};

/**
 * The reason to show beside a status select, for the statuses it has greyed
 * out. A disabled menu item carries no tooltip of its own and the menu is shut
 * when the reason matters, so the whole control wears it.
 *
 * Only the queue order earns a tooltip: it names the truck ahead, which the
 * user cannot see from the greyed items alone. DEPARTED greyed on an undocked
 * row gets no tooltip — the rule is self-evident once the truck is docked, and
 * a tooltip over the whole select covered the values in the open menu.
 */
export const gateStatusRestriction = ({
  currentStatus,
  orderBound,
  isNextInQueue,
  nextLabel,
}) => {
  if (orderBound && !isNextInQueue && currentStatus === ARRIVED)
    return outOfTurnTooltip(nextLabel);
  return null;
};
